import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ServiceAddOn } from '../../types/Service';
import { closeIcon as CloseIcon, plusIcon as PlusIcon } from './DiningMenuIcons';
import { MAX_ORDER_QUANTITY } from './diningMenuUtils';

export type DishDetailTag = {
    key: string;
    label: string;
    className: string;
};

export type DishDetailViewModel = {
    id: string;
    name: string;
    mealTypeLabel: string;
    restaurantName?: string;
    shortDescription?: string;
    longDescription?: string;
    imageUrl?: string;
    itemCost?: number;
    tags: DishDetailTag[];
    addons?: ServiceAddOn[];
};

interface Props {
    open: boolean;
    dish: DishDetailViewModel | null;
    getOrderBlockedMessage?: (replaceCartId?: string) => string | undefined;
    onClose: () => void;
    onAddToOrder: (addons: ServiceAddOn[], quantity: number, replaceCartId?: string) => boolean;
}

const BannerPlaceholderIcon = () => (
    <svg
        width="90"
        height="90"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
    </svg>
);

/**
 * Presentational dish detail / add-to-order modal.
 * Parent owns open state and maps ServicesType → DishDetailViewModel.
 */
const DishDetailModal = ({
    open,
    dish,
    getOrderBlockedMessage,
    onClose,
    onAddToOrder,
}: Props) => {
    const [selectedAddonIds, setSelectedAddonIds] = useState<Set<string>>(new Set());
    const [quantity, setQuantity] = useState(1);
    const dishId = dish?.id ?? dish?.name ?? '';
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Re-init quantity/add-ons only when the modal opens or the dish identity
    // changes — not on parent re-renders that pass a new addons [] / onClose.
    useEffect(() => {
        if (!open || !dish) return;
        const requiredIds = (dish.addons || []).filter((a) => a.required).map((a) => a.id);
        setSelectedAddonIds(new Set(requiredIds));
        setQuantity(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dishId]);

    useEffect(() => {
        if (!open) return undefined;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCloseRef.current();
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    if (!open || !dish) return null;

    const addons = dish.addons || [];
    const shortDescription = dish.shortDescription?.trim() || '';
    const longDescription = dish.longDescription?.trim() || '';
    const showLongDescription = Boolean(longDescription) && longDescription !== shortDescription;
    const itemCost = Number(dish.itemCost) || 0;

    const toggleAddon = (addon: ServiceAddOn) => {
        if (addon.required) return;
        setSelectedAddonIds((prev) => {
            const next = new Set(prev);
            if (next.has(addon.id)) next.delete(addon.id);
            else next.add(addon.id);
            return next;
        });
    };

    const selectedAddons = addons.filter((addon) => selectedAddonIds.has(addon.id) || addon.required);
    // Fresh modal opens are always adds (additive merge by signature). Absolute
    // quantity set is reserved for edit-in-place via replaceCartId.
    const orderBlockedMessage = getOrderBlockedMessage?.();

    const handleAddClick = () => {
        if (orderBlockedMessage) return;
        const added = onAddToOrder(selectedAddons, quantity);
        if (added) onClose();
    };

    return createPortal(
        <div
            className="dish-overlay"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="dish-modal" role="dialog" aria-modal="true" aria-labelledby="dish-name">
                <div className="dish-card">
                    <div className="dish-banner">
                        {dish.mealTypeLabel ? (
                            <div className="dish-meal-badge">{dish.mealTypeLabel}</div>
                        ) : null}
                        <button type="button" className="dish-close" onClick={onClose} aria-label="Close">
                            <CloseIcon />
                        </button>
                        {dish.imageUrl ? (
                            <img src={dish.imageUrl} alt={dish.name} />
                        ) : (
                            <div className="dish-banner-icon">
                                <BannerPlaceholderIcon />
                            </div>
                        )}
                    </div>

                    <div className="dish-modal-body">
                        {dish.restaurantName ? (
                            <div className="dish-restaurant-name">{dish.restaurantName}</div>
                        ) : null}
                        <div className="dish-name" id="dish-name">
                            {dish.name}
                        </div>
                        {dish.tags.length > 0 ? (
                            <div className="dish-tags">
                                {dish.tags.map((tag) => (
                                    <span key={tag.key} className={`item-tag ${tag.className || 'tag-neutral'}`}>
                                        {tag.label}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                        {shortDescription ? (
                            <div className="dish-desc dish-desc-short">{shortDescription}</div>
                        ) : null}
                        {showLongDescription ? (
                            <div className="dish-desc">{longDescription}</div>
                        ) : null}
                        {itemCost > 0 ? (
                            <div className="dish-price">${itemCost.toFixed(2)}</div>
                        ) : null}
                    </div>
                </div>

                <div className="dish-addons-label">Add-Ons</div>
                <div className="dish-addons-list">
                    {addons.length > 0 ? (
                        addons.map((addon) => {
                            const checked = selectedAddonIds.has(addon.id) || addon.required;
                            return (
                                <button
                                    key={addon.id}
                                    type="button"
                                    className={`dish-addon-item${checked ? ' checked' : ''}`}
                                    onClick={() => toggleAddon(addon)}
                                    disabled={addon.required}
                                >
                                    <span className="dish-addon-checkbox" aria-hidden="true">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    </span>
                                    <span className="dish-addon-name">{addon.itemName}</span>
                                    {addon.itemCost ? (
                                        <span className="dish-addon-price">+${Number(addon.itemCost).toFixed(2)}</span>
                                    ) : null}
                                </button>
                            );
                        })
                    ) : (
                        <div className="dish-addons-empty">No add-ons available for this item.</div>
                    )}
                </div>

                <div className="dish-qty-row">
                    <div className="qty-stepper">
                        <button
                            type="button"
                            aria-label="Decrease quantity"
                            disabled={quantity <= 1}
                            onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                        </button>
                        <span className="qty-stepper-value">{quantity}</span>
                        <button
                            type="button"
                            aria-label="Increase quantity"
                            disabled={quantity >= MAX_ORDER_QUANTITY}
                            onClick={() => setQuantity((prev) => Math.min(MAX_ORDER_QUANTITY, prev + 1))}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {orderBlockedMessage ? (
                    <p className="dish-order-blocked-msg" role="alert">
                        {orderBlockedMessage}
                    </p>
                ) : null}

                <button
                    type="button"
                    className="dish-order-btn"
                    disabled={Boolean(orderBlockedMessage)}
                    onClick={handleAddClick}
                >
                    <PlusIcon />
                    {quantity > 1 ? `Add ${quantity} to Order` : 'Add to Order'}
                </button>
            </div>
        </div>,
        document.body,
    );
};

export default DishDetailModal;
